import React from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { FaChevronDown } from 'react-icons/fa';
import { MdCheck } from 'react-icons/md';
import { ExperienceSpinner } from '@/components/experience-spinner';

export interface BlockStatusRowProps {
  label: string;
  completed: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  children?: React.ReactNode;
  width?: string;
}

export const BlockStatusRow = ({
  label,
  completed,
  expandable = false,
  expanded = false,
  onToggleExpand,
  children,
  width,
}: BlockStatusRowProps) => (
  <Container
    className="markdown-block-status-row"
    style={{ width: width || `100%` }}
    layout
    initial={{ opacity: 0, y: 8, height: 0 }}
    animate={{ opacity: 1, y: 0, height: `auto` }}
    exit={{ opacity: 0, y: -8, height: 0, marginBottom: 0 }}
    transition={{ duration: 0.28, ease: `easeOut` }}
  >
    <Header
      type="button"
      onClick={expandable ? onToggleExpand : undefined}
      style={{ cursor: expandable ? `pointer` : `default` }}
    >
      <StatusText>{label}</StatusText>
      <StatusRight>
        <AnimatePresence mode="wait" initial={false}>
          {completed ? (
            <StatusDoneIcon
              key="done"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, ease: `easeOut` }}
            >
              <MdCheck />
            </StatusDoneIcon>
          ) : (
            <ExperienceSpinner size={1.5} color="#8b8b8b" />
          )}
        </AnimatePresence>
        {expandable && (
          <ChevronIcon
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.18, ease: `easeOut` }}
          >
            <FaChevronDown />
          </ChevronIcon>
        )}
      </StatusRight>
    </Header>
    <AnimatePresence initial={false}>
      {expandable && expanded && children && (
        <ExpandContent
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: `auto` }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: `easeOut` }}
        >
          {children}
        </ExpandContent>
      )}
    </AnimatePresence>
  </Container>
);

export const BlockStatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-top: 1rem;
  width: 70%;
`;

const Container = styled(motion.div)`
  border-radius: 1rem;
  border: 1px solid rgba(17, 17, 17, 0.12);
  background: rgba(248, 249, 251, 0.8);
  overflow: hidden;
`;

const Header = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.2rem;
`;

const StatusRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
`;

const StatusText = styled.span`
  font-family: Eesti, sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: #4c5560;
`;

const StatusDoneIcon = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #3ea76a;
  font-size: 1.8rem;
  flex-shrink: 0;
`;

const ChevronIcon = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #4c5560;
  font-size: 1.1rem;
`;

export const ExpandContent = styled(motion.div)`
  overflow: hidden;
`;

export const ExpandPre = styled.pre`
  margin: 0;
  border-top: 1px solid rgba(17, 17, 17, 0.08);
  background: #f8f9fb;
  padding: 1.2rem;
  overflow: auto;
`;

export const ExpandCode = styled.code`
  font-family: 'JetBrains Mono', monospace;
  color: #111;
  font-size: 1.3rem;
  line-height: 1.45;
  white-space: pre-wrap;
`;
